import { useState, useEffect } from 'react';
import { AlertTriangle, Skull, Clock, Activity, XCircle, Heart, CheckCircle2, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';

// Componente de alerta de deterioro individual
const DeteriorationAlertItem = ({ alert, onDismiss }) => {
  const getAlertConfig = (type) => {
    switch(type) {
      case 'critical':
        return {
          icon: Skull,
          bgColor: 'bg-red-600/90',
          borderColor: 'border-red-400',
          textColor: 'text-white',
          pulseClass: 'animate-pulse'
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-amber-600/90',
          borderColor: 'border-amber-400',
          textColor: 'text-white',
          pulseClass: ''
        };
      case 'omission':
        return {
          icon: XCircle,
          bgColor: 'bg-orange-600/90',
          borderColor: 'border-orange-400',
          textColor: 'text-white',
          pulseClass: ''
        };
      default:
        return {
          icon: AlertTriangle,
          bgColor: 'bg-slate-700/90',
          borderColor: 'border-slate-500',
          textColor: 'text-white',
          pulseClass: ''
        };
    }
  };

  const config = getAlertConfig(alert.type);
  const Icon = config.icon;

  return (
    <div 
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg border-2 backdrop-blur-sm shadow-xl",
        config.bgColor,
        config.borderColor,
        config.pulseClass
      )}
      data-testid={`deterioration-alert-${alert.type}`}
    >
      <div className="flex-shrink-0">
        <Icon size={24} className={config.textColor} />
      </div>
      <div className="flex-1">
        <p className={cn("font-semibold text-sm", config.textColor)}>
          {alert.message}
        </p>
        {alert.time_remaining !== undefined && alert.time_remaining > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <Clock size={14} className="text-white/80" />
            <span className="text-xs text-white/80">
              Tiempo restante: {Math.floor(alert.time_remaining / 60)}:{(alert.time_remaining % 60).toString().padStart(2, '0')}
            </span>
          </div>
        )}
        {alert.actions_needed && (
          <div className="mt-2">
            <span className="text-xs text-white/80">Acciones requeridas:</span>
            <ul className="list-disc list-inside text-xs text-white/90 mt-1">
              {alert.actions_needed.map((action, idx) => (
                <li key={idx}>{action}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {onDismiss && (
        <button 
          onClick={onDismiss}
          className="flex-shrink-0 p-1 hover:bg-black/20 rounded"
        >
          <XCircle size={16} className="text-white/60" />
        </button>
      )}
    </div>
  );
};

// Panel principal de alertas de deterioro
const DeteriorationAlertsPanel = ({ alerts = [], onDismissAlert }) => {
  if (!alerts || alerts.length === 0) return null;

  return (
    <div 
      className="fixed top-20 left-4 z-50 flex flex-col gap-2 max-w-md w-full pointer-events-auto"
      data-testid="deterioration-alerts-panel"
    >
      {alerts.map((alert, index) => (
        <DeteriorationAlertItem 
          key={`${alert.type}-${index}`}
          alert={alert}
          onDismiss={() => onDismissAlert?.(index)}
        />
      ))}
    </div>
  );
};

// Alerta de recuperación exitosa
const RecoveryAlert = ({ recoveryData, onDismiss }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Auto-dismiss después de 5 segundos
    const timer = setTimeout(() => {
      setVisible(false);
      if (onDismiss) onDismiss();
    }, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  if (!visible || !recoveryData) return null;

  return (
    <div 
      className={cn(
        "fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-lg w-full",
        "animate-in slide-in-from-top-5 fade-in-0 duration-500"
      )}
      data-testid="recovery-alert"
    >
      <div className="bg-emerald-600/95 border-2 border-emerald-400 rounded-xl p-5 shadow-2xl backdrop-blur-sm">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-14 h-14 bg-emerald-500/30 rounded-full flex items-center justify-center">
            <Heart size={32} className="text-white animate-pulse" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={18} className="text-yellow-300" />
              <h3 className="text-lg font-bold text-white">
                PACIENTE EN RECUPERACIÓN
              </h3>
              <Sparkles size={18} className="text-yellow-300" />
            </div>
            <p className="text-white/90 text-sm mb-3">
              {recoveryData.message || "El paciente responde positivamente a la intervención"}
            </p>
            {recoveryData.improvements && Object.keys(recoveryData.improvements).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(recoveryData.improvements).map(([vital, data]) => (
                  <div 
                    key={vital}
                    className="bg-emerald-700/50 px-3 py-1 rounded-full text-xs text-white flex items-center gap-1"
                  >
                    <CheckCircle2 size={12} />
                    <span className="uppercase">{vital}:</span>
                    <span>{data.from} → {data.to}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button 
            onClick={() => {
              setVisible(false);
              if (onDismiss) onDismiss();
            }}
            className="flex-shrink-0 p-1 hover:bg-emerald-700/50 rounded transition-colors"
          >
            <XCircle size={18} className="text-white/70" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Pantalla de muerte del paciente
const DeathScreen = ({ causeOfDeath, elapsedTime, onViewDebriefing, onEndSimulation }) => {
  const [showScreen, setShowScreen] = useState(false);

  useEffect(() => {
    // Pequeño delay para animación
    const timer = setTimeout(() => setShowScreen(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className={cn(
        "fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm transition-opacity duration-500",
        showScreen ? "opacity-100" : "opacity-0"
      )}
      data-testid="death-screen"
    >
      <div className={cn(
        "bg-slate-900 border-2 border-red-600 rounded-xl p-8 max-w-lg w-full mx-4 shadow-2xl transform transition-all duration-500",
        showScreen ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
      )}>
        {/* Icono y título */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-20 h-20 bg-red-600/20 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <Skull size={48} className="text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-red-500 mb-2">
            PACIENTE FALLECIDO
          </h2>
          <div className="flex items-center gap-2 text-slate-400">
            <Clock size={16} />
            <span>Tiempo de simulación: {formatTime(elapsedTime)}</span>
          </div>
        </div>

        {/* Causa de muerte */}
        <div className="bg-red-950/50 border border-red-800 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-red-400 uppercase mb-2">
            Causa de Fallecimiento
          </h3>
          <p className="text-white text-lg">
            {causeOfDeath || "Paro cardiorrespiratorio sin respuesta a maniobras de reanimación"}
          </p>
        </div>

        {/* Mensaje educativo */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Activity size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-amber-400 mb-1">
                Reflexión Clínica
              </h4>
              <p className="text-sm text-slate-300">
                Este resultado es parte del aprendizaje. Revisa el debriefing para identificar 
                las intervenciones críticas que pudieron haber cambiado el desenlace.
              </p>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex flex-col gap-3">
          <Button 
            onClick={onViewDebriefing}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="view-debriefing-btn"
          >
            Ver Debriefing y Análisis
          </Button>
          <Button 
            onClick={onEndSimulation}
            variant="outline"
            className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="end-simulation-btn"
          >
            Finalizar Simulación
          </Button>
        </div>
      </div>
    </div>
  );
};

export { DeteriorationAlertsPanel, DeathScreen, DeteriorationAlertItem, RecoveryAlert };
export default DeteriorationAlertsPanel;
